/**
 * `capture` — which contact fields the form asks for, and the consent line
 * (decisions/consent-language.md). The consent text is stored verbatim on
 * every lead, so it is shown here exactly as the visitor will read it.
 *
 * Not shown, though stored values are kept: `capture.timing` (the widget
 * always asks after the diagnosis, when the visitor chooses to send it) and
 * `capture.consent.trustedform` (the widget captures no TrustedForm
 * certificate). The SMS opt-in IS live: the widget shows the box and the
 * lead's consent record keeps the answer and the text shown.
 */
import { consumerDirectedConsentText } from '../../../../diagnosis-widget/src/consentText';
import { useSession } from '../../auth/session';
import { CONTACT_FIELDS } from '../model';
import { FORMATTING_HINT, NullableText, PathSelect, PathSwitch, SectionCard, SettingsTabLink, usePath } from './common';

const FIELD_LABELS: Record<string, string> = {
  first_name: 'First name',
  last_name: 'Last name',
  phone_1: 'Phone',
  email: 'Email',
  address_1: 'Street address',
  address_2: 'Apt / unit',
  city: 'City',
  state: 'State',
  postal_code: 'ZIP code',
  preferred_contact: 'Preferred contact method',
  preferred_window: 'Preferred time window',
};

type Need = 'required' | 'optional' | 'off';

export function ContactFieldsTable() {
  const required = usePath<string[]>('capture.fields.required');
  const optional = usePath<string[]>('capture.fields.optional');
  const prefill = usePath<string[]>('capture.prefill_from_identity_token');
  const issues = usePath<unknown>('capture.fields').issues;
  const needOf = (f: string): Need => (required.value.includes(f) ? 'required' : optional.value.includes(f) ? 'optional' : 'off');
  const setNeed = (f: string, need: Need) => {
    const order = (list: string[]) => CONTACT_FIELDS.filter((x) => list.includes(x));
    required.set(order(need === 'required' ? [...required.value, f] : required.value.filter((x) => x !== f)));
    optional.set(order(need === 'optional' ? [...optional.value, f] : optional.value.filter((x) => x !== f)));
  };
  return (
    <div className="stack-sm">
      <div className="table-wrap">
        <table className="data">
          <caption className="visually-hidden">Contact fields</caption>
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Ask for it</th>
              <th scope="col">Fill from your login system</th>
            </tr>
          </thead>
          <tbody>
            {CONTACT_FIELDS.map((f) => (
              <tr key={f}>
                <th scope="row" style={{ fontWeight: 600, textTransform: 'none', letterSpacing: 0, fontSize: 13, color: 'var(--ink)', position: 'static' }}>
                  {FIELD_LABELS[f]}
                </th>
                <td>
                  <select
                    className="input"
                    style={{ minHeight: 30, padding: '3px 8px', maxWidth: 150 }}
                    aria-label={`${FIELD_LABELS[f]}: ask for it`}
                    value={needOf(f)}
                    onChange={(e) => setNeed(f, e.target.value as Need)}
                  >
                    <option value="required">Required</option>
                    <option value="optional">Optional</option>
                    <option value="off">Don’t ask</option>
                  </select>
                </td>
                <td>
                  {/* A 20 px box in a 36 px target (third live review: 13 px boxes). */}
                  <label className="cell-check">
                    <input
                      type="checkbox"
                      aria-label={`Fill ${FIELD_LABELS[f]} from your login system`}
                      checked={prefill.value.includes(f)}
                      onChange={(e) =>
                        prefill.set(CONTACT_FIELDS.filter((x) => (x === f ? e.target.checked : prefill.value.includes(x))))
                      }
                    />
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="small muted">
        Fill from your login system: for visitors who are signed in to your site, the form starts with what you already know about
        them. Your developer sets this up (<SettingsTabLink tab="placement">Placement → Advanced</SettingsTabLink>).
      </p>
      {issues.length ? (
        <p className="err small" role="alert">
          {issues.map((i) => i.message).join(' ')}
        </p>
      ) : null}
    </div>
  );
}

export function ConsentEditor() {
  const { org } = useSession();
  const mode = usePath<'consumer_directed' | 'pewc'>('capture.consent.mode');
  return (
    <div className="stack">
      <PathSelect
        path="capture.consent.mode"
        label="Consent model"
        hint="Consumer-directed: a line next to the phone field; tapping Send is the agreement. Written consent adds an unticked checkbox."
        options={[
          { value: 'consumer_directed', label: 'Consumer-directed (default)' },
          { value: 'pewc', label: 'Prior express written consent (checkbox)' },
        ]}
      />
      <NullableText
        path="capture.consent.text_md"
        label="Consent text"
        multiline
        placeholder={mode.value === 'pewc' ? 'Required for written consent' : consumerDirectedConsentText(org?.display_name ?? 'the team')}
        hint={`Stored word for word with every lead, as your record of what the visitor agreed to. ${FORMATTING_HINT}`}
      />
      <PathSwitch
        path="capture.consent.sms_opt_in"
        label="Separate SMS opt-in"
        description="An extra, unticked box so visitors can agree to text messages from you. Their answer is saved with the lead’s consent record."
      />
    </div>
  );
}

export function CaptureSection() {
  return (
    <SectionCard id="sec-capture" title="Contact form & consent" description="What the contact form asks for, and what the visitor agrees to.">
      <ContactFieldsTable />
      <hr className="divider" />
      <ConsentEditor />
    </SectionCard>
  );
}
